import { LoadingOverlayComponent } from '../../shared/components/loading-overlay/loading-overlay.component';

import { AuthService } from '../../auth/auth.service';
import { SmwService } from '../../smw/smw.service';
import { LanguageService } from 'src/app/shared/services/language.service';

import { DatexPipe } from '../../shared/datex.pipe';
import { GetColorPipe } from '../../smw/pipes/get-color.pipe';

import { WmIconDirective } from '../../shared/wm-icon.directive';

import { CommonModule } from '@angular/common';
import {
  Component,
  effect,
  inject,
  Injector,
  input,
  OnInit,
  output,
  signal,
  untracked,
} from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';

import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { FaIconComponent } from '@fortawesome/angular-fontawesome';

import moment from 'moment';
import { Moment } from 'moment';
import { getColor } from 'src/app/smw/constants/constants';
import { isNil } from 'src/utils/utils';

export const MODULES = [
  CommonModule,
  MatTooltipModule,
  RouterModule,
  TranslateModule,
];
export const COMPONENTS = [FaIconComponent];
export const PIPES = [DatexPipe, GetColorPipe];
export const DIRECTIVES = [WmIconDirective];

@Component({
  selector: 'app-dashboard-smw',
  templateUrl: './dashboard-smw.component.html',
  styleUrls: ['./dashboard-smw.component.scss', '../dashboard.component.scss'],
  imports: [
    ...MODULES,
    ...COMPONENTS,
    ...PIPES,
    ...DIRECTIVES,
    LoadingOverlayComponent,
  ],
})
export class DashboardSmwComponent implements OnInit {
  private injector = inject(Injector);
  private auth = inject(AuthService);
  public smwService = inject(SmwService);
  public translate = inject(TranslateService);
  public languageService = inject(LanguageService);

  smwConfig = input<any>();
  refresh = input<number>();
  // Wenn gesetzt, werden diese gespeicherten Messwerte angezeigt statt live
  // abgerufen (z.B. beim erneuten Öffnen eines abgeschlossenen Einsatzes).
  snapshotData = input<any>(null);
  // Rohantwort des aktuell angezeigten Standes (egal ob live geladen oder
  // Snapshot) – ermöglicht dem Elternteil, sie z.B. beim Speichern eines
  // neuen Einsatzprotokolls als Snapshot zu übernehmen.
  smwResponseChange = output<any>();

  previousRefresh = signal(null);
  gmaAlarmStations = [];
  webcamStations = [];
  latestWebcamImages = signal({});
  sortedStations = signal([]);
  smwData = signal<any>(undefined);
  isLoading = signal(true);
  latestDtg: Moment;

  constructor() {
    this.refreshSmwData();
  }

  ngOnInit(): void {
    this.readColorScheme();

    const snapshotData = this.snapshotData();
    if (snapshotData) {
      // Webcam-Bilder sind immer der aktuellste Stand, nicht der zum
      // Speicherzeitpunkt – für eine eingefrorene historische Ansicht daher
      // bewusst nicht mit abgerufen (findStationWithWebcam()), nur die
      // Stationsliste/GMA-Berechtigung (strukturell, nicht zeitabhängig).
      this.findStationWithGmaAlarm();
      this.sortStations();
      this.smwData.set(snapshotData);
      this.isLoading.set(false);
      this.smwResponseChange.emit(snapshotData);
      return;
    }

    this.findStationWithWebcam();
    this.findStationWithGmaAlarm();
    this.getLatestSmwData();
  }

  updateWebcamImages(): void {
    for (const stationId of Object.keys(this.latestWebcamImages())) {
      this.getLatestWebcamImage(stationId);
    }
  }

  readColorScheme(): void {
    const storedColor = localStorage.getItem('eopTableColorScheme');
    let color;
    if (storedColor) {
      color = storedColor;
    } else {
      const userProfile = this.auth.getUserProfile();
      const global = userProfile['metadata']['global'];
      if (global && global.hasOwnProperty('eopTableColorScheme')) {
        color = global['eopTableColorScheme'];
      }
    }
    this.smwService.greenTableDesign.set(color === 'green');
  }

  getLatestSmwData(): void {
    const chunk = this.smwConfig().stations.slice(0, 10);
    this.smwService.getLatestDataForStations(chunk).subscribe(
      data => {
        const latestDtg = this.latestSmwDtg(data);
        if (!this.latestDtg || latestDtg.isAfter(this.latestDtg)) {
          if (this.latestDtg) {
            this.updateWebcamImages();
          }
          this.latestDtg = latestDtg;
          this.smwData.set(undefined);
          this.isLoading.set(true);
          this.smwData.set({ ...data });
          this.isLoading.set(false);
          this.smwResponseChange.emit({ ...data });
        }
      },
      error => console.log('Error DashboardSMW getLatestSmwData', error)
    );
  }

  latestSmwDtg(smwData): Moment {
    let latest: Moment;
    for (const stationId of Object.keys(smwData)) {
      const dtg = moment(smwData[stationId]['dtg']);
      if (!latest || latest.isBefore(dtg)) {
        latest = dtg;
      }
    }
    return latest;
  }

  findStationWithWebcam(): void {
    const smwConfig = this.smwConfig();
    if (smwConfig.stationType === 'gma') {
      this.smwService.getWebcamStatus(smwConfig.stations).subscribe(
        data => {
          for (const stationId in data) {
            if (data.hasOwnProperty(stationId)) {
              if (data[stationId]) {
                this.webcamStations.push(stationId);
                this.getLatestWebcamImage(stationId);
              }
            }
          }
          this.sortStations();
        },
        error => console.log('Error DashboardSMW findStationWithWebcam', error)
      );
    } else {
      this.sortStations();
    }
  }

  findStationWithGmaAlarm(): void {
    const smwConfig = this.smwConfig();
    this.gmaAlarmStations = [];
    if (smwConfig.stationType === 'gma') {
      if (this.auth.isProductActive('egwGmaProducts')) {
        for (const stationId of smwConfig.stations) {
          if (this.smwService.isGmaAlarmForStationOrdered(stationId, false)) {
            this.gmaAlarmStations.push(stationId);
          }
        }
      }
    }
  }

  sortStations(): void {
    const unsortedStations = [];
    const smwConfig = this.smwConfig();
    for (const stationId of smwConfig.stations) {
      unsortedStations.push({
        id: stationId,
        name: smwConfig.stationNames[stationId],
        webcam: this.webcamStations.indexOf(stationId) >= 0,
        gmaAlarm: this.gmaAlarmStations.indexOf(stationId) >= 0,
      });
    }
    const sortedStationsByName = unsortedStations.sort((a, b) =>
      a.name > b.name ? 1 : -1
    );
    const withWebcam = sortedStationsByName.filter(s => s.webcam);
    const withoutWebcam = sortedStationsByName.filter(s => !s.webcam);
    this.sortedStations.set([...withWebcam.concat(withoutWebcam)]);
  }

  getLatestWebcamImage(stationId): void {
    this.smwService.getWebcamImages(stationId, 1).subscribe(
      data => {
        const dtg = moment(data[0]['dtg']);
        const url = data[0]['url'];
        if (dtg.isAfter(moment().subtract(2, 'hours'))) {
          this.latestWebcamImages.update(latestWebcamImages => {
            return {
              ...latestWebcamImages,
              [stationId]: this.smwService.transformWebcamUrl(url),
            };
          });
        } else {
          this.latestWebcamImages.update(latestWebcamImages => {
            return {
              ...latestWebcamImages,
              [stationId]: null,
            };
          });
        }
      },
      err => console.error('getLatestWebcamImage', err)
    );
  }

  getColor(data: any, parameter: string): string {
    return getColor(data, parameter, this.smwService.greenTableDesign());
  }

  private refreshSmwData() {
    effect(
      () => {
        const refresh = this.refresh();
        untracked(() => {
          const previousRefresh = this.previousRefresh();
          if (!isNil(previousRefresh) && refresh > previousRefresh) {
            if (this.smwData()) {
              this.getLatestSmwData();
            }
          }
          this.previousRefresh.set(refresh);
        });
      },
      { injector: this.injector }
    );
  }
}
